// =============================================================================
// staticSite/designBrief — system designu per domena (motyw „brand").
//
// Brief = paleta + para czcionek + motyw dekoracji + teksty landingowe. Powstaje
// z AI (designBriefGenerator, prompt static_design_brief) albo deterministycznie
// z seeda domeny (fallback bez kosztu). Zapis: static_sites.settings.design.
// Czysty modul — wspoldzielony z build-workerem i szablonem (BuildInput.design).
// Czcionki TYLKO z puli Fontsource (hostowane w obrazie, zero Google Fonts).
// =============================================================================

export type DesignMode = "dark" | "light";
export type DesignMotif = "network" | "waves" | "grid" | "rings" | "blobs" | "diagonal";

export interface DesignPalette {
  /** Tlo strony */
  bg: string;
  /** Tlo kart / paneli */
  panel: string;
  /** Tekst glowny */
  ink: string;
  /** Tekst drugorzedny */
  muted: string;
  /** Obramowania */
  border: string;
  /** 3 akcenty: glowny (linki, CTA), drugi, trzeci (moduly, okladki) */
  accent: string;
  accent2: string;
  accent3: string;
}

export interface DesignFonts {
  heading: FontId;
  body: FontId;
  mono: FontId;
}

export interface DesignBrief {
  version: 1;
  mode: DesignMode;
  palette: DesignPalette;
  fonts: DesignFonts;
  motif: DesignMotif;
  radius: 0 | 4 | 8 | 14;
  hero: { eyebrow: string; headline: string; subheadline: string; cta: string };
  labels: { modules: string; modulesIntro: string; knowledge: string; about: string; ctaTitle: string; ctaButton: string };
  /** Skad brief: ai | seed | manual (edytowany w panelu) */
  source: "ai" | "seed" | "manual";
  rationale?: string;
  generatedAt?: string;
}

// -----------------------------------------------------------------------------
// Pula czcionek (Fontsource, licencje OFL) — id -> pakiet npm + family CSS
// -----------------------------------------------------------------------------

export interface FontDef {
  id: FontId;
  label: string;
  pkg: string;
  /** Sciezka importu CSS w szablonie (index.css = wszystkie wagi / variable) */
  css: string;
  family: string;
  role: Array<"heading" | "body" | "mono">;
  vibe: string;
}

export type FontId =
  | "space-grotesk" | "syne" | "unbounded" | "bricolage" | "archivo-black" | "sora" | "outfit" | "schibsted"
  | "fraunces" | "instrument-serif"
  | "manrope" | "inter" | "plus-jakarta" | "instrument-sans" | "dm-sans" | "atkinson"
  | "jetbrains-mono" | "space-mono";

export const FONT_POOL: FontDef[] = [
  { id: "space-grotesk", label: "Space Grotesk", pkg: "@fontsource-variable/space-grotesk", css: "@fontsource-variable/space-grotesk", family: "'Space Grotesk Variable'", role: ["heading", "body"], vibe: "tech, nowoczesny, geometryczny" },
  { id: "syne", label: "Syne", pkg: "@fontsource-variable/syne", css: "@fontsource-variable/syne", family: "'Syne Variable'", role: ["heading"], vibe: "odwazny, artystyczny, display" },
  { id: "unbounded", label: "Unbounded", pkg: "@fontsource-variable/unbounded", css: "@fontsource-variable/unbounded", family: "'Unbounded Variable'", role: ["heading"], vibe: "szeroki, mocny, futurystyczny" },
  { id: "bricolage", label: "Bricolage Grotesque", pkg: "@fontsource-variable/bricolage-grotesque", css: "@fontsource-variable/bricolage-grotesque", family: "'Bricolage Grotesque Variable'", role: ["heading", "body"], vibe: "charakterny grotesk, redakcyjny" },
  { id: "archivo-black", label: "Archivo Black", pkg: "@fontsource/archivo-black", css: "@fontsource/archivo-black", family: "'Archivo Black'", role: ["heading"], vibe: "bardzo gruby, plakatowy" },
  { id: "sora", label: "Sora", pkg: "@fontsource-variable/sora", css: "@fontsource-variable/sora", family: "'Sora Variable'", role: ["heading", "body"], vibe: "czysty, produktowy, SaaS" },
  { id: "outfit", label: "Outfit", pkg: "@fontsource-variable/outfit", css: "@fontsource-variable/outfit", family: "'Outfit Variable'", role: ["heading", "body"], vibe: "miekki geometryczny, lifestyle" },
  { id: "schibsted", label: "Schibsted Grotesk", pkg: "@fontsource-variable/schibsted-grotesk", css: "@fontsource-variable/schibsted-grotesk", family: "'Schibsted Grotesk Variable'", role: ["heading", "body"], vibe: "prasowy, rzeczowy" },
  { id: "fraunces", label: "Fraunces", pkg: "@fontsource-variable/fraunces", css: "@fontsource-variable/fraunces", family: "'Fraunces Variable'", role: ["heading"], vibe: "szeryf z charakterem, premium, food/lifestyle" },
  { id: "instrument-serif", label: "Instrument Serif", pkg: "@fontsource/instrument-serif", css: "@fontsource/instrument-serif", family: "'Instrument Serif'", role: ["heading"], vibe: "elegancki szeryf, magazyn" },
  { id: "manrope", label: "Manrope", pkg: "@fontsource-variable/manrope", css: "@fontsource-variable/manrope", family: "'Manrope Variable'", role: ["body", "heading"], vibe: "neutralny, czytelny" },
  { id: "inter", label: "Inter", pkg: "@fontsource-variable/inter", css: "@fontsource-variable/inter", family: "'Inter Variable'", role: ["body"], vibe: "uniwersalny, interfejsowy" },
  { id: "plus-jakarta", label: "Plus Jakarta Sans", pkg: "@fontsource-variable/plus-jakarta-sans", css: "@fontsource-variable/plus-jakarta-sans", family: "'Plus Jakarta Sans Variable'", role: ["body", "heading"], vibe: "przyjazny, zaokraglony" },
  { id: "instrument-sans", label: "Instrument Sans", pkg: "@fontsource-variable/instrument-sans", css: "@fontsource-variable/instrument-sans", family: "'Instrument Sans Variable'", role: ["body"], vibe: "redakcyjny sans, lekki" },
  { id: "dm-sans", label: "DM Sans", pkg: "@fontsource-variable/dm-sans", css: "@fontsource-variable/dm-sans", family: "'DM Sans Variable'", role: ["body", "heading"], vibe: "geometryczny, spokojny" },
  { id: "atkinson", label: "Atkinson Hyperlegible", pkg: "@fontsource/atkinson-hyperlegible", css: "@fontsource/atkinson-hyperlegible", family: "'Atkinson Hyperlegible'", role: ["body"], vibe: "maksymalna czytelnosc" },
  { id: "jetbrains-mono", label: "JetBrains Mono", pkg: "@fontsource-variable/jetbrains-mono", css: "@fontsource-variable/jetbrains-mono", family: "'JetBrains Mono Variable'", role: ["mono"], vibe: "kod, etykiety techniczne" },
  { id: "space-mono", label: "Space Mono", pkg: "@fontsource/space-mono", css: "@fontsource/space-mono", family: "'Space Mono'", role: ["mono"], vibe: "retro-tech, etykiety" },
];

export const FONT_BY_ID: Record<string, FontDef> = Object.fromEntries(FONT_POOL.map((f) => [f.id, f]));
export const DESIGN_MOTIFS: DesignMotif[] = ["network", "waves", "grid", "rings", "blobs", "diagonal"];

export function fontFamily(id: FontId, fallback = "system-ui, sans-serif"): string {
  const f = FONT_BY_ID[id];
  return f ? `${f.family}, ${fallback}` : fallback;
}

// -----------------------------------------------------------------------------
// Presety (fallback bez AI + punkt startowy dla edycji)
// -----------------------------------------------------------------------------

interface Preset {
  mode: DesignMode;
  palette: DesignPalette;
  fonts: DesignFonts;
  motif: DesignMotif;
  radius: DesignBrief["radius"];
}

const PRESETS: Preset[] = [
  // arcade — granat + neon (inspiracja: strona szefa)
  { mode: "dark", palette: { bg: "#070b28", panel: "#0b1034", ink: "#f2f3ff", muted: "#aeb4e0", border: "#1d2454", accent: "#365bff", accent2: "#c7ff3d", accent3: "#ff3d81" }, fonts: { heading: "unbounded", body: "atkinson", mono: "jetbrains-mono" }, motif: "network", radius: 4 },
  // ember — grafit + pomarancz
  { mode: "dark", palette: { bg: "#0f0e0c", panel: "#181613", ink: "#f5efe6", muted: "#a89f92", border: "#2a2622", accent: "#ff7a1a", accent2: "#ffd166", accent3: "#4cc9f0" }, fonts: { heading: "bricolage", body: "manrope", mono: "space-mono" }, motif: "diagonal", radius: 8 },
  // forest — ciemna zielen + limonka
  { mode: "dark", palette: { bg: "#08120f", panel: "#0e1c17", ink: "#eaf5ef", muted: "#97ada2", border: "#1c2f28", accent: "#5ce39a", accent2: "#e4ff5c", accent3: "#ffa8d6" }, fonts: { heading: "syne", body: "plus-jakarta", mono: "jetbrains-mono" }, motif: "waves", radius: 14 },
  // paper — jasny, redakcyjny
  { mode: "light", palette: { bg: "#fbf9f4", panel: "#ffffff", ink: "#161512", muted: "#6b665c", border: "#e6e1d6", accent: "#c8361c", accent2: "#1c5fc8", accent3: "#e0a51a" }, fonts: { heading: "fraunces", body: "instrument-sans", mono: "jetbrains-mono" }, motif: "rings", radius: 4 },
  // studio — jasny, geometryczny
  { mode: "light", palette: { bg: "#f6f7fb", panel: "#ffffff", ink: "#111827", muted: "#5b6472", border: "#e2e6ef", accent: "#3b4cf0", accent2: "#00b894", accent3: "#ff5c8a" }, fonts: { heading: "sora", body: "inter", mono: "space-mono" }, motif: "grid", radius: 14 },
  // mono — czarno-bialy + jeden akcent
  { mode: "dark", palette: { bg: "#0a0a0a", panel: "#141414", ink: "#fafafa", muted: "#9a9a9a", border: "#262626", accent: "#f5f5f5", accent2: "#ffe600", accent3: "#8a8a8a" }, fonts: { heading: "archivo-black", body: "dm-sans", mono: "space-mono" }, motif: "blobs", radius: 0 },
];

function byteAt(seed: string, i: number): number {
  const idx = (i * 2) % Math.max(2, seed.length);
  return parseInt(seed.slice(idx, idx + 2), 16) || 0;
}

/** Deterministyczny brief z seeda domeny (sha256 hex) — bez AI, bez kosztu. */
export function seedDesignBrief(seedHex: string, ctx: { siteName: string; tagline?: string | null; firstCategory?: string | null }): DesignBrief {
  const seed = /^[0-9a-f]{64}$/i.test(seedHex) ? seedHex.toLowerCase() : "0".repeat(64);
  const preset = PRESETS[byteAt(seed, 20) % PRESETS.length];
  const name = ctx.siteName || "Strona";
  return {
    version: 1,
    mode: preset.mode,
    palette: { ...preset.palette },
    fonts: { ...preset.fonts },
    motif: preset.motif,
    radius: preset.radius,
    hero: {
      eyebrow: "Wiedza i praktyka",
      headline: ctx.tagline || name,
      subheadline: ctx.tagline ? `${name} — artykuły, poradniki i sprawdzone metody.` : "Artykuły, poradniki i sprawdzone metody w jednym miejscu.",
      cta: ctx.firstCategory ? `Zobacz: ${ctx.firstCategory}` : "Czytaj artykuły",
    },
    labels: {
      modules: "Tematy",
      modulesIntro: "Obszary, które opisujemy najdokładniej.",
      knowledge: "Najnowsze artykuły",
      about: "O autorze",
      ctaTitle: "Zacznij od najważniejszego",
      ctaButton: "Przejdź do artykułów",
    },
    source: "seed",
  };
}

// -----------------------------------------------------------------------------
// Normalizacja (wynik AI / edycja w panelu) — nigdy nie ufamy surowemu JSON-owi
// -----------------------------------------------------------------------------

const HEX_RE = /^#[0-9a-f]{6}$/i;

function hex(v: unknown, fallback: string): string {
  if (typeof v !== "string") return fallback;
  const s = v.trim();
  if (HEX_RE.test(s)) return s.toLowerCase();
  const short = s.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase();
  return fallback;
}

function text(v: unknown, fallback: string, max = 140): string {
  if (typeof v !== "string") return fallback;
  const s = v.replace(/\s+/g, " ").trim();
  return s ? s.slice(0, max) : fallback;
}

function fontId(v: unknown, role: "heading" | "body" | "mono", fallback: FontId): FontId {
  const f = typeof v === "string" ? FONT_BY_ID[v.trim().toLowerCase()] : null;
  return f && f.role.includes(role) ? f.id : fallback;
}

/** Sklada poprawny brief z dowolnego wejscia; braki uzupelnia z `base`. */
export function normalizeDesignBrief(input: unknown, base: DesignBrief, source: DesignBrief["source"]): DesignBrief {
  const r = (input && typeof input === "object" ? input : {}) as Record<string, any>;
  const p = (r.palette && typeof r.palette === "object" ? r.palette : {}) as Record<string, unknown>;
  const f = (r.fonts && typeof r.fonts === "object" ? r.fonts : {}) as Record<string, unknown>;
  const h = (r.hero && typeof r.hero === "object" ? r.hero : {}) as Record<string, unknown>;
  const l = (r.labels && typeof r.labels === "object" ? r.labels : {}) as Record<string, unknown>;
  const mode: DesignMode = r.mode === "light" ? "light" : r.mode === "dark" ? "dark" : base.mode;
  const radiusNum = Number(r.radius);
  const radius = ([0, 4, 8, 14] as const).includes(radiusNum as 0 | 4 | 8 | 14) ? (radiusNum as DesignBrief["radius"]) : base.radius;
  const motif = DESIGN_MOTIFS.includes(r.motif) ? (r.motif as DesignMotif) : base.motif;
  return {
    version: 1,
    mode,
    palette: {
      bg: hex(p.bg, base.palette.bg),
      panel: hex(p.panel, base.palette.panel),
      ink: hex(p.ink, base.palette.ink),
      muted: hex(p.muted, base.palette.muted),
      border: hex(p.border, base.palette.border),
      accent: hex(p.accent, base.palette.accent),
      accent2: hex(p.accent2, base.palette.accent2),
      accent3: hex(p.accent3, base.palette.accent3),
    },
    fonts: {
      heading: fontId(f.heading, "heading", base.fonts.heading),
      body: fontId(f.body, "body", base.fonts.body),
      mono: fontId(f.mono, "mono", base.fonts.mono),
    },
    motif,
    radius,
    hero: {
      eyebrow: text(h.eyebrow, base.hero.eyebrow, 60),
      headline: text(h.headline, base.hero.headline, 90),
      subheadline: text(h.subheadline, base.hero.subheadline, 220),
      cta: text(h.cta, base.hero.cta, 50),
    },
    labels: {
      modules: text(l.modules, base.labels.modules, 40),
      modulesIntro: text(l.modulesIntro, base.labels.modulesIntro, 160),
      knowledge: text(l.knowledge, base.labels.knowledge, 40),
      about: text(l.about, base.labels.about, 40),
      ctaTitle: text(l.ctaTitle, base.labels.ctaTitle, 90),
      ctaButton: text(l.ctaButton, base.labels.ctaButton, 50),
    },
    source,
    rationale: typeof r.rationale === "string" ? r.rationale.slice(0, 300) : base.rationale,
    generatedAt: new Date().toISOString(),
  };
}

/** Kontrast WCAG (relative luminance) — do ostrzezen w panelu. */
export function contrastRatio(a: string, b: string): number {
  const lum = (h: string) => {
    const c = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  };
  const [x, y] = [lum(a), lum(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
